import React, { useState, useEffect, useRef } from 'react';
import Portal from './Portal';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * Non-dismissible indeterminate processing modal for long-running external operations.
 * Theme-aware (admin = red, employee = blue). Designed for operations where no
 * granular progress data is available (e.g. external API calls, OCR, PDF generation).
 *
 * Features:
 *   - Rotating status messages on a configurable interval
 *   - Asymptotic progress estimate (% bar) -- climbs fast then slows,
 *     capped at 95% so we never falsely claim completion. Matches user
 *     perception of long-running operations (OS-installer pattern) far
 *     better than an open-ended elapsed counter alone. The modal closing
 *     IS the 100% signal -- by then the page is rendered.
 *   - Live elapsed-time counter (kept as a small secondary detail)
 *   - Pulsing ring animation (not a spinner — communicates "working", not "almost done")
 *   - No close/dismiss button (non-cancellable)
 *   - Error state with dismiss callback
 *
 * Props:
 *   isOpen              – boolean
 *   title               – string  (heading text)
 *   subtitle            – string | null  (optional secondary line below the title)
 *   theme               – 'admin' | 'employee'  (default 'admin')
 *   statusMessages      – string[]  (rotate on interval; at least 2 recommended)
 *   rotateInterval      – number  (ms between message rotations, default 8000)
 *   estimatedDurationMs – number | null  (expected total runtime, used to
 *                         calibrate the % curve so it lands at ~90% near
 *                         the expected end and crawls toward 95%
 *                         afterwards. Defaults to
 *                         `statusMessages.length * rotateInterval`, which
 *                         is the team's existing notion of "expected
 *                         duration" encoded by the message rotation.)
 *   icon                – ReactNode | null  (optional icon rendered in the pulse ring)
 *   error               – string | null  (if set, shows error state with dismiss)
 *   onDismissError      – () => void
 */
const ProcessingModal = ({
  isOpen,
  title = 'Processing',
  subtitle = null,
  theme = 'admin',
  statusMessages = ['Processing...'],
  rotateInterval = 8000,
  estimatedDurationMs = null,
  icon = null,
  error = null,
  onDismissError,
}) => {
  const [messageIdx, setMessageIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setMessageIdx(0);
      setElapsed(0);
      startRef.current = Date.now();
    }
  }, [isOpen]);

  // Elapsed timer (ticks every second)
  useEffect(() => {
    if (!isOpen || error) return;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen, error]);

  // Message rotation
  useEffect(() => {
    if (!isOpen || error || statusMessages.length <= 1) return;
    const id = setInterval(() => {
      setMessageIdx((prev) => (prev + 1) % statusMessages.length);
    }, rotateInterval);
    return () => clearInterval(id);
  }, [isOpen, error, statusMessages, rotateInterval]);

  // iOS-proof body scroll lock (see useBodyScrollLock).
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const isAdmin = theme === 'admin';
  const accentRing = isAdmin ? 'border-red-200' : 'border-blue-200';
  const accentPulse = isAdmin ? 'bg-red-500' : 'bg-blue-600';
  const accentText = isAdmin ? 'text-red-600' : 'text-blue-700';
  const accentBg = isAdmin ? 'bg-red-50' : 'bg-blue-50';
  const dismissBtnClass = isAdmin ? 'btn btn-admin-primary' : 'btn btn-primary';
  const focusRingClass = isAdmin
    ? 'focus-visible:ring-red-500'
    : 'focus-visible:ring-blue-500';

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec.toString().padStart(2, '0')}s` : `${sec}s`;
  };

  // Asymptotic progress estimate. Real progress is unknowable (no backend
  // signal for these operations), but a curve that climbs fast then slows
  // matches user perception far better than an open-ended elapsed counter.
  // Math: pct(t) = CAP * (1 - exp(-t / tau)), with tau = expected/3 so
  // that at t = expected we land at ~90% (since 1 - e^-3 ≈ 0.95). After
  // that it crawls asymptotically toward CAP (= 95%), which is the
  // industry-standard "almost there" pattern (installers, CI runners,
  // YouTube uploads). We never display 100% during loading because the
  // modal closing IS the 100% signal -- claiming 100% while the user is
  // still waiting would be the lie.
  const expectedSec = Math.max(
    1,
    (estimatedDurationMs ?? statusMessages.length * rotateInterval) / 1000
  );
  const tauSec = expectedSec / 3;
  const PROGRESS_CAP = 95;
  const pct = Math.round(PROGRESS_CAP * (1 - Math.exp(-elapsed / tauSec)));

  return (
    <Portal>
      <div
        className="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processing-modal-title"
        aria-live="polite"
      >
        <div
          className="modal-container modal-container-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="flex flex-col items-center text-center py-4">

              {/* Pulsing ring indicator */}
              {!error && (
                <div className="relative mb-6">
                  {/* Outer pulse ring */}
                  <div className={`absolute inset-0 rounded-full ${accentBg} animate-ping opacity-30`}
                    style={{ animationDuration: '2s' }}
                  />
                  {/* Static ring */}
                  <div className={`relative w-16 h-16 rounded-full border-4 ${accentRing} flex items-center justify-center`}>
                    {icon ? (
                      <div className={accentText}>{icon}</div>
                    ) : (
                      <>
                        {/* Animated dot */}
                        <div className={`w-3 h-3 rounded-full ${accentPulse}`}
                          style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Error icon */}
              {error && (
                <div className="mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Title */}
              <h3 id="processing-modal-title" className="text-lg font-semibold text-gray-900 mb-1">
                {error ? 'Processing Failed' : title}
              </h3>

              {/* Subtitle */}
              {!error && subtitle && (
                <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
              )}

              {/* Rotating status message */}
              {!error && (
                <div className="h-5 mb-4">
                  <p
                    key={messageIdx}
                    className={`text-sm font-medium ${accentText} fade-in`}
                  >
                    {statusMessages[messageIdx]}
                  </p>
                </div>
              )}

              {/* Progress bar with asymptotic % estimate -- gives users the
                  "loading X%" mental model they expect, even though the
                  underlying operation is indeterminate. Elapsed time is
                  preserved as a smaller secondary cue. */}
              {!error && (
                <div className="w-full mt-2">
                  <div
                    className="h-2 w-full bg-gray-100 rounded-full overflow-hidden"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Loading progress: ${pct}%`}
                  >
                    <div
                      className={`h-full ${accentPulse} rounded-full transition-all duration-700 ease-out`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span
                      className={`text-sm font-semibold ${accentText} tabular-nums`}
                      aria-live="polite"
                    >
                      {pct}%
                    </span>
                    <span className="text-xs text-gray-500 tabular-nums">
                      Elapsed {formatElapsed(elapsed)}
                    </span>
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mt-2 mb-2 w-full text-left">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer — only on error */}
          {error && onDismissError && (
            <div className="modal-footer">
              <button
                type="button"
                onClick={onDismissError}
                className={`${dismissBtnClass} focus-visible:outline-none focus-visible:ring-2 ${focusRingClass} focus-visible:ring-offset-2`}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default ProcessingModal;
